import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, Typography } from '@mui/material';
import { callAPI, getLocalValue, LoacalVariables, Method } from '../../commonFunctions/CommonFunctions';
import RepaymentHistorySkeleton from '../components/skeletons/repaymentHistorySkeleton';
import { useNavigate } from 'react-router-dom';

export interface RepaymentHistoryProps {
    month: number;
    dueDate: string; // ISO string, can convert to Date if needed
    scheduledEMI: number;
    scheduledPrincipal: number;
    scheduledInterest: number;
    status: 'Paid' | 'Unpaid' | 'Partial'; // enum enforced
    actualPayment?: {
        _id: string;
        receiptNumber: string;
        amount: number;
        principal: number;
        interest: number;
        repaymentDate: string; // ISO date string
        remarks: string;
    };
}

interface ApiResponse<T> {
    success: boolean;
    data: T;
    message?: string;
}

const LoanRepaymentTable = ({ loanId, loanStatus, fetchRepayment, handleCorrectionClick }: { loanId: string, loanStatus: string, fetchRepayment: boolean, handleCorrectionClick: (item: any) => void; }) => {
    const [repayments, setRepayments] = useState<RepaymentHistoryProps[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(5);

    const userType = getLocalValue(LoacalVariables.UserType);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchRepaymentSchedules = async () => {
            setLoading(true);
            try {
                const response: ApiResponse<RepaymentHistoryProps[]> = await callAPI(
                    "repayments/getRepaymentHistory",
                    Method.GET,
                    {},
                    loanId
                );

                console.log('Repayment history response:', response);
                if (response.success) {
                    setRepayments(response.data);
                }

            } catch (error: any) {
                console.error("Failed to retrieve repayment data", error);
            } finally {
                setLoading(false);
            }
        };

        fetchRepaymentSchedules();
    }, [loanId, fetchRepayment]);

    const filteredRepayments = useMemo(() => {
        return repayments.filter(
            (repayment: any) => repayment.actualPayment !== null
        );
    }, [repayments]);

    const paginatedRepayments = useMemo(() => {
        return filteredRepayments.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage
        );
    }, [filteredRepayments, page, rowsPerPage]);

    if (loading) return <RepaymentHistorySkeleton />;

    return (
        <>
            {repayments.length > 0 && (
                <Paper sx={{ padding: 2, marginTop: 3, marginBottom: 5 }}>
                    <Typography variant="h6" gutterBottom>
                        Repayment History
                    </Typography>

                    {/* ADD: Summary of additional payments */}
                    {repayments.some((rep: any) => rep.month === 'Additional') && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                Includes {repayments.filter((rep: any) => rep.month === 'Additional').length}
                                additional payment(s) made beyond the original schedule
                            </Typography>
                        </Alert>
                    )}

                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>EMI Month</TableCell>
                                <TableCell>Receipt No</TableCell>
                                <TableCell>Repayment Date</TableCell>
                                <TableCell align="right">Paid Amount (₹)</TableCell>
                                <TableCell align="right">Principal</TableCell>
                                {/* <TableCell align="right">Interest</TableCell> */}
                                <TableCell>Remarks</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedRepayments.map((repayment: any, idx: number) => {
                                const {
                                    actualPayment,
                                    dueDate,
                                    status,
                                    month,
                                    scheduledEMI,
                                    scheduledPrincipal,
                                    scheduledInterest
                                } = repayment;

                                const showCorrectionButton =
                                    status === 'Paid' &&
                                    actualPayment &&
                                    userType === 'block' &&
                                    loanStatus !== 'completed';

                                const handleReceiptClick = () => {
                                    if (actualPayment?._id) {
                                        navigate(`/loan/repayment/details/${actualPayment._id}`);
                                    }
                                };

                                // ADD: Determine if this is an additional payment
                                const isAdditionalPayment = month === 'Additional';

                                // ADD: Check if payment matches scheduled EMI
                                const isExactMatch = !isAdditionalPayment &&
                                    actualPayment?.amount === scheduledEMI;

                                return (
                                    <TableRow key={idx}>
                                        {/* ADD: EMI Month with additional payment indicator */}
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {isAdditionalPayment ? 'Additional Payment' : `Month ${month}`}
                                                </Typography>
                                                {!isAdditionalPayment && !isExactMatch && (
                                                    <Typography variant="caption" color="warning.main">
                                                        Different from scheduled
                                                    </Typography>
                                                )}
                                                {!isAdditionalPayment && (
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        Due: {new Date(dueDate).toLocaleDateString()}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>

                                        <TableCell
                                            onClick={handleReceiptClick}
                                            style={{
                                                cursor: 'pointer',
                                                color: '#1976d2',
                                                textDecoration: "underline"
                                            }}
                                        >
                                            {actualPayment?.receiptNumber || '-'}
                                        </TableCell>

                                        <TableCell>
                                            {(actualPayment?.repaymentDate
                                                ? new Date(actualPayment.repaymentDate)
                                                : new Date(dueDate)
                                            ).toLocaleDateString()}
                                        </TableCell>

                                        <TableCell align="right">
                                            <Box>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {actualPayment?.amount?.toLocaleString() || '-'}
                                                </Typography>
                                                {!isAdditionalPayment && scheduledEMI > 0 && (
                                                    <Typography
                                                        variant="caption"
                                                        color={actualPayment?.amount === scheduledEMI ? "success.main" : "warning.main"}
                                                    >
                                                        {actualPayment?.amount === scheduledEMI ?
                                                            'As scheduled' :
                                                            `Scheduled: ₹${scheduledEMI}`
                                                        }
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>

                                        <TableCell align="right">
                                            <Box>
                                                <Typography variant="body2">
                                                    {actualPayment?.principal?.toLocaleString() || '-'}
                                                </Typography>
                                                {!isAdditionalPayment && scheduledPrincipal > 0 && (
                                                    <Typography
                                                        variant="caption"
                                                        color={actualPayment?.principal === scheduledPrincipal ? "text.secondary" : "warning.main"}
                                                    >
                                                        {actualPayment?.principal === scheduledPrincipal ?
                                                            '' :
                                                            `Scheduled: ₹${scheduledPrincipal}`
                                                        }
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>

                                        {/* <TableCell align="right">
                                                <Box>
                                                    <Typography variant="body2">
                                                        {actualPayment?.interest?.toLocaleString() || '-'}
                                                    </Typography>
                                                    {!isAdditionalPayment && scheduledInterest > 0 && (
                                                        <Typography
                                                            variant="caption"
                                                            color={actualPayment?.interest === scheduledInterest ? "text.secondary" : "warning.main"}
                                                        >
                                                            {actualPayment?.interest === scheduledInterest ?
                                                                '' :
                                                                `Scheduled: ₹${scheduledInterest}`
                                                            }
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell> */}

                                        <TableCell>
                                            {actualPayment?.remarks || '-'}
                                        </TableCell>

                                        <TableCell>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="warning"
                                                onClick={() => handleCorrectionClick(repayment)}
                                                sx={{ textTransform: 'none', ml: 1 }}
                                            >
                                                🛠 Request Correction
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>

                    <TablePagination
                        component="div"
                        count={filteredRepayments.length}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                    />
                </Paper>
            )}
        </>
    );
};

export default LoanRepaymentTable;
